import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiClientError } from '../../services/apiClient'
import { useAuth } from '../../auth/AuthContext'
import {
  reportesClient,
  STATUS_LABELS,
  type ReportListFilters,
  type ReportListItem,
  type ReportStatus,
} from '../../services/reportesClient'

const PAGE_SIZE = 25
const FILTER_DEBOUNCE_MS = 300

/**
 * Lista de reportes (§7.1 SRS, REP-01..04).
 *
 *   - 25 reportes por defecto + lazy load.
 *   - Búsqueda combinada: folio LIKE OR alias del cliente LIKE.
 *   - Filtros: rango de fechas + estado.
 *   - Click en fila → /reportes/{id}.
 *   - Reportes cerrados muestran "Ver detalle" en vez de "Editar" (REP-04 v1.3).
 */
export function ReportesPage() {
  const navigate = useNavigate()
  const { state } = useAuth()
  const currentUserId = state.status === 'authenticated' ? state.user.id : null

  const [items, setItems] = useState<ReportListItem[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [total, setTotal] = useState<number | null>(null)

  const [q, setQ] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [status, setStatus] = useState<ReportStatus | ''>('')
  const [debouncedFilters, setDebouncedFilters] = useState<ReportListFilters>({})

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // Debounce de los filtros
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters({
        q: q.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        status: status || undefined,
      })
    }, FILTER_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [q, dateFrom, dateTo, status])

  // Carga inicial / cuando cambian filtros
  const fetchFirstPage = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const page = await reportesClient.list(debouncedFilters, null, PAGE_SIZE)
      setItems(page.items)
      setCursor(page.next_cursor)
      setHasMore(page.next_cursor !== null)
      setTotal(page.total)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.')
    } finally {
      setLoading(false)
    }
  }, [debouncedFilters])

  useEffect(() => {
    fetchFirstPage()
  }, [fetchFirstPage])

  // Lazy load
  useEffect(() => {
    if (!hasMore || cursor === null) return
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading) loadMore(cursor)
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, hasMore, loading])

  async function loadMore(from: number) {
    setLoading(true)
    try {
      const page = await reportesClient.list(debouncedFilters, from, PAGE_SIZE)
      setItems((prev) => [...prev, ...page.items])
      setCursor(page.next_cursor)
      setHasMore(page.next_cursor !== null)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar más.')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate() {
    setCreating(true)
    setError(null)
    try {
      const res = await reportesClient.create({})
      navigate(`/reportes/${res.report.id}`)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo crear el reporte.')
      setCreating(false)
    }
  }

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Reportes</h1>
          {total !== null && <span className="muted">{total.toLocaleString('es-MX')} reportes</span>}
        </div>
        <div className="list-toolbar__actions">
          <button
            type="button"
            className="primary-btn"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? 'Creando…' : '+ Nuevo reporte'}
          </button>
        </div>
      </header>

      <div className="reportes-filters">
        <input
          type="search"
          className="search-input"
          placeholder="Buscar por folio o cliente…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar reportes"
        />
        <label className="filter-field">
          <span>Desde</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="filter-field">
          <span>Hasta</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="filter-field">
          <span>Estado</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as ReportStatus | '')}>
            <option value="">Todos</option>
            <option value="draft">Borrador</option>
            <option value="pdf_generated">PDF generado</option>
            <option value="closed">Cerrado</option>
          </select>
        </label>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Folio</th>
              <th scope="col">Cliente</th>
              <th scope="col">Elaborado por</th>
              <th scope="col">Fecha</th>
              <th scope="col">Estado</th>
              <th scope="col" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="data-table__empty">
                  No hay reportes que coincidan. Crea el primero con "Nuevo reporte".
                </td>
              </tr>
            )}
            {items.map((r) => {
              const canEdit = r.created_by === null || r.created_by === currentUserId
              return (
                <tr
                  key={r.id}
                  className="data-table__row"
                  tabIndex={0}
                  onClick={() => navigate(`/reportes/${r.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/reportes/${r.id}`)
                    }
                  }}
                >
                  <td><strong className="mono">{r.folio}</strong></td>
                  <td>{r.client_alias ?? <span className="muted">Sin cliente</span>}</td>
                  <td className="muted">{r.created_by_name ?? '—'}</td>
                  <td>{formatDate(r.start_date ?? r.created_at)}</td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="data-table__actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="ghost-btn small"
                      onClick={() => navigate(`/reportes/${r.id}`)}
                    >
                      {r.status === 'closed' || !canEdit ? 'Ver' : 'Abrir'}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {loading && <p className="muted center">Cargando…</p>}
        {hasMore && cursor !== null && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />}
        {!hasMore && items.length > 0 && (
          <p className="muted center">Has llegado al final de la lista.</p>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: ReportStatus }) {
  return <span className={`badge badge--${status}`}>{STATUS_LABELS[status]}</span>
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  try {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return value
  }
}
